import { IconButton } from "@/components/interactive/IconButton";
import { Tooltip } from "@/components/TooltipV2";
import { useLayoutEffect, useRef } from "react";
import toast, { resolveValue, ToastBar, type Toast, type ToastPosition } from "react-hot-toast";

export const ToastCard: React.FC<{
  notification: Toast;
  position: ToastPosition;
  offset: number;
  updateHeight: (id: string, height: number) => void;
  onFocus: () => void;
  onBlur: (event: React.FocusEvent<HTMLDivElement>) => void;
  onHover: (hovered: boolean) => void;
}> = ({ notification, position, offset, updateHeight, onFocus, onBlur, onHover }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const measure = () => updateHeight(notification.id, element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [notification.id, updateHeight]);
  const top = position.startsWith("top");
  return (
    <div
      ref={ref}
      data-toast-id={notification.id}
      inert={!notification.visible}
      aria-hidden={!notification.visible || undefined}
      onFocusCapture={onFocus}
      onBlurCapture={onBlur}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onKeyDown={(event) => {
        // Activation belongs to the toast control, not a dialog's document-level Enter listener.
        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
      }}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        ...(top ? { top: 0 } : { bottom: 0 }),
        display: "flex",
        justifyContent: position.endsWith("center")
          ? "center"
          : position.endsWith("right")
            ? "flex-end"
            : "flex-start",
        transform: `translateY(${offset * (top ? 1 : -1)}px)`,
        pointerEvents: "none",
      }}
    >
      <div style={{ maxWidth: "100%", pointerEvents: notification.visible ? "auto" : "none" }}>
        {notification.type === "custom" ? (
          resolveValue(notification.message, notification)
        ) : (
          <ToastBar toast={notification} position={position}>
            {({ icon, message }) => (
              <div className="flex min-w-0 items-start gap-1">
                {icon && <div className="mt-[6px] shrink-0">{icon}</div>}
                <div className="min-w-0">{message}</div>
                {notification.type !== "loading" && (
                  <div className="shrink-0">
                    <Tooltip content="Dismiss (esc)">
                      <IconButton
                        type="button"
                        aria-label="Dismiss notification"
                        size="xs"
                        onClick={() => toast.dismiss(notification.id)}
                        iconName="close"
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
          </ToastBar>
        )}
      </div>
    </div>
  );
};
